import React, {
  ComponentPropsWithoutRef,
  ElementType,
  ReactNode,
  useCallback,
} from 'react';
import classnames from 'classnames';
import ReactSelect, { components } from 'react-select';

import { ReactComponent as ChevronDownSVG } from '../../../../assets/icons/chevron-down.svg';
import { ReactComponent as MarkSVG } from '../../../../assets/icons/mark.svg';

import './Days.scss';

const getShortDayName = (day) => day.substr(0, 3);

export enum DayValues {
  monday = 'monday',
  tuesday = 'tuesday',
  wednesday = 'wednesday',
  thursday = 'thursday',
  friday = 'friday',
  saturday = 'saturday',
  sunday = 'sunday',
}

const WEEKDAYS = [
  DayValues.monday,
  DayValues.tuesday,
  DayValues.wednesday,
  DayValues.thursday,
  DayValues.friday,
];

const WEEKEND = [DayValues.saturday, DayValues.sunday];

type DayLabels =
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday'
  | 'Sunday';

interface DayOption {
  label: DayLabels;
  value: DayValues;
}

export const DAYS_OPTIONS: DayOption[] = [
  { label: 'Monday', value: DayValues.monday },
  { label: 'Tuesday', value: DayValues.tuesday },
  { label: 'Wednesday', value: DayValues.wednesday },
  { label: 'Thursday', value: DayValues.thursday },
  { label: 'Friday', value: DayValues.friday },
  { label: 'Saturday', value: DayValues.saturday },
  { label: 'Sunday', value: DayValues.sunday },
];

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <ChevronDownSVG />
  </components.DropdownIndicator>
);

interface OptionProps extends ComponentPropsWithoutRef<ElementType> {
  children: ReactNode;
  isSelected: boolean;
}

const Option = ({ children, isSelected, ...props }: OptionProps) => {
  return (
    <components.Option isSelected={isSelected} {...props}>
      <div className="days__option-text">{children}</div>
      {isSelected && (
        <div className="days__option-selected-icon">
          <MarkSVG />
        </div>
      )}
    </components.Option>
  );
};

interface ValueContainerProps extends ComponentPropsWithoutRef<ElementType> {
  hasValue?: boolean;
}

const ValueContainer = (props: ValueContainerProps) => {
  if (!props.hasValue) {
    return <components.ValueContainer {...props} />;
  }

  const value = props.getValue();

  let content;

  if (value.length === DAYS_OPTIONS.length) {
    content = 'Every day';
  } else if (
    value.length === WEEKDAYS.length &&
    value.every(({ value: v }) => WEEKDAYS.includes(v))
  ) {
    content = 'Weekdays';
  } else if (
    value.length === WEEKEND.length &&
    value.every(({ value: v }) => WEEKEND.includes(v))
  ) {
    content = 'Weekend';
  } else {
    content = value.map((v) => getShortDayName(v.label)).join(', ');
  }

  return (
    <components.ValueContainer {...props}>{content}</components.ValueContainer>
  );
};

export interface DaysProps extends ComponentPropsWithoutRef<ElementType> {
  className?: string;
  value?: string[];
  onChange: (val: DayValues[]) => void;
}

const Days = ({ className, value = [], onChange, ...props }: DaysProps) => {
  const handleChange = useCallback(
    (values: DayOption[]) => {
      if (onChange) {
        onChange(values.map(({ value }) => value));
      }
    },
    [onChange],
  );

  return (
    <ReactSelect
      className={classnames('days', className)}
      classNamePrefix="days"
      options={DAYS_OPTIONS}
      isClearable={false}
      isSearchable={false}
      isMulti={true}
      hideSelectedOptions={false}
      closeMenuOnSelect={false}
      placeholder="Days"
      components={{ DropdownIndicator, Option, ValueContainer }}
      menuPosition="fixed"
      closeMenuOnScroll={true}
      value={DAYS_OPTIONS.filter((option) => value.includes(option.value))}
      onChange={handleChange}
      {...props}
    />
  );
};

export default Days;
